/**
 * Webhook-failures hooks — #935 PR-D.
 *
 * Thin TanStack Query wrappers around `webhookFailuresAPI` (repointed to
 * `/api/webhook-failures/*`). The bulk and purge mutations invalidate
 * `queryKeys.webhookFailures.all()` on success so the inbox list + badge
 * refresh together, plus the open per-order trace views (the trace
 * endpoint surfaces `webhook_failure` events from the same table).
 *
 * The single-row mutations (resolve / replay / delete) run as resource-kit
 * row actions, and the kit runner already refreshes the `webhook-failures`
 * namespace after each success; when the action closes the detail, that
 * refresh does not read the closed detail again (#2802). So these hooks
 * refresh only the trace
 * views, which sit outside that namespace. Invalidating the namespace here
 * too would refetch a detail that is still open, and a deleted failure's
 * detail answers 404.
 *
 * The new list surface filters via the shared kernel (`page` / `size` /
 * `sort` / `search`); the count endpoint takes no params and reports the
 * global OPEN-failure total. Adds the `replay` mutation (#935): replays
 * a stored failure through the live ingestion core.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/queryKeys';
import {
  webhookFailuresAPI,
  type ListParams,
  type WebhookFailureBulkDeleteResult,
  type WebhookFailureBulkResolveResult,
  type WebhookFailureByTraceResponse,
  type WebhookFailureDeleteResult,
  type WebhookFailureDetail,
  type WebhookFailureListResponse,
  type WebhookFailurePurgeResult,
  type WebhookFailureReplayResult,
  type WebhookFailureResolveResult,
} from '@/services/webhook-failures';

/** 5 seconds — the inbox is operator-actionable so freshness matters. */
const LIST_STALE_MS = 5_000;
/** 30s focused-tab refetch; quiet in the background. */
const BACKGROUND_REFETCH_MS = 30_000;

/** Default first page — keep in lockstep with the page's initial state. */
const DEFAULT_PAGE = 1;
const DEFAULT_SIZE = 50;

type NormalisedListParams = Required<Pick<ListParams, 'page' | 'size'>> &
  Pick<ListParams, 'sort' | 'search' | 'extraParams'>;

/** Collapse semantically-equivalent shapes to one cache key. */
function normaliseListParams(params: ListParams): NormalisedListParams {
  return {
    page: params.page ?? DEFAULT_PAGE,
    size: params.size ?? DEFAULT_SIZE,
    sort: params.sort,
    search: params.search,
    // Thread the whitelisted QueryBuilder filters through so a filter change
    // both re-keys the cache (triggers refetch) and reaches `list()` (#1389).
    extraParams: params.extraParams,
  };
}

export function useWebhookFailuresList(params: ListParams = {}, options?: { enabled?: boolean }) {
  const normalised = normaliseListParams(params);
  return useQuery<WebhookFailureListResponse, Error>({
    queryKey: queryKeys.webhookFailures.list(normalised),
    queryFn: () => webhookFailuresAPI.list(normalised),
    enabled: options?.enabled ?? true,
    staleTime: LIST_STALE_MS,
    refetchInterval: BACKGROUND_REFETCH_MS,
    refetchIntervalInBackground: false,
  });
}

/**
 * Re-exported from `@hooks/useWebhookFailuresCount`, which is where it lives
 * now (#2005): `features/webhooks` needs the same number for the Failures tab
 * badge, and reaching into this feature for it would close a barrel cycle.
 * Kept exported here so this hook module stays the one import path an admin
 * caller (or a `vi.mock` of it) has to know.
 */
export { useWebhookFailuresCount } from '@hooks/useWebhookFailuresCount';

export function useWebhookFailureDetail(id: string, options?: { enabled?: boolean }) {
  return useQuery<WebhookFailureDetail, Error>({
    queryKey: queryKeys.webhookFailures.detail(id),
    queryFn: () => webhookFailuresAPI.getById(id),
    enabled: options?.enabled ?? true,
  });
}

export function useWebhookFailuresByTrace(traceId: string, options?: { enabled?: boolean }) {
  return useQuery<WebhookFailureByTraceResponse, Error>({
    queryKey: queryKeys.webhookFailures.byTrace(traceId),
    queryFn: () => webhookFailuresAPI.listByTrace(traceId),
    enabled: options?.enabled ?? true,
  });
}

/**
 * Invalidate the webhook-failures namespace + the open trace views.
 * `Promise.allSettled` so a failing post-mutation refetch does NOT flip
 * a successful resolve/replay into an operator-visible failure.
 */
async function invalidateRelated(qc: ReturnType<typeof useQueryClient>): Promise<void> {
  const results = await Promise.allSettled([
    qc.invalidateQueries({ queryKey: queryKeys.webhookFailures.all() }),
    qc.invalidateQueries({ queryKey: queryKeys.orders.traces() }),
  ]);
  for (const r of results) {
    if (r.status === 'rejected') {
      console.warn('[webhook-failures] post-mutation invalidation refetch failed', r.reason);
    }
  }
}

/**
 * Invalidate only the open trace views. The single-row mutations use this:
 * the kit runner owns the `webhook-failures` namespace refresh (#2802).
 */
function invalidateTraces(qc: ReturnType<typeof useQueryClient>): Promise<void> {
  return qc.invalidateQueries({ queryKey: queryKeys.orders.traces() });
}

interface IdMutationInput {
  id: string;
}

export function useResolveWebhookFailure() {
  const qc = useQueryClient();
  return useMutation<WebhookFailureResolveResult, Error, IdMutationInput>({
    mutationFn: ({ id }) => webhookFailuresAPI.resolve(id),
    onSuccess: () => invalidateTraces(qc),
  });
}

interface BulkResolveMutationInput {
  ids: string[];
}

export function useBulkResolveWebhookFailures() {
  const qc = useQueryClient();
  return useMutation<WebhookFailureBulkResolveResult, Error, BulkResolveMutationInput>({
    mutationFn: ({ ids }) => webhookFailuresAPI.bulkResolve(ids),
    onSuccess: () => invalidateRelated(qc),
  });
}

/**
 * Replay a stored failure through the live ingestion core (#935). On a
 * successful (2xx) replay the failure is closed; like resolve, the hook
 * refreshes the trace views and the kit runner refreshes the inbox. Error
 * outcomes (payload unavailable / still broken / still failing) reject
 * through the thrown `ApiError` and the page maps them to a closed-set
 * outcome message off `error.code`.
 */
export function useReplayWebhookFailure() {
  const qc = useQueryClient();
  return useMutation<WebhookFailureReplayResult, Error, IdMutationInput>({
    mutationFn: ({ id }) => webhookFailuresAPI.replay(id),
    onSuccess: () => invalidateTraces(qc),
  });
}

/**
 * Hard-delete a single failure (#1363/#1364). Removing the row drops it from
 * the inbox + count badge. The kit runner refreshes that namespace without
 * reading the deleted failure's detail again; the hook refreshes the trace
 * views only, so it never refetches the deleted detail (#2802).
 */
export function useDeleteWebhookFailure() {
  const qc = useQueryClient();
  return useMutation<WebhookFailureDeleteResult, Error, IdMutationInput>({
    mutationFn: ({ id }) => webhookFailuresAPI.deleteById(id),
    onSuccess: () => invalidateTraces(qc),
  });
}

export function useBulkDeleteWebhookFailures() {
  const qc = useQueryClient();
  return useMutation<WebhookFailureBulkDeleteResult, Error, BulkResolveMutationInput>({
    mutationFn: ({ ids }) => webhookFailuresAPI.bulkDelete(ids),
    onSuccess: () => invalidateRelated(qc),
  });
}

interface PurgeMutationInput {
  before?: string;
}

/** Wholesale drop of the failure log (the "clear all" affordance, #1364). */
export function usePurgeWebhookFailures() {
  const qc = useQueryClient();
  return useMutation<WebhookFailurePurgeResult, Error, PurgeMutationInput>({
    mutationFn: ({ before }) => webhookFailuresAPI.purge(before),
    onSuccess: () => invalidateRelated(qc),
  });
}
